<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.back()" class="tl-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="m-0"><i class="mdi mdi-timeline-text-outline me-2"></i>Life Timeline</h4>
        </div>
        <button class="tl-btn tl-btn-blue" @click="load"><i class="mdi mdi-refresh"></i></button>
      </div>

      <div v-if="!data.patient" class="tl-empty">
        <i class="mdi mdi-loading mdi-spin"></i> Loading…
      </div>

      <div v-else>
        <!-- HEADER -->
        <div class="tl-header">
          <div class="tl-header-main">
            <h2 class="tl-name">{{ data.patient.first_name }} {{ data.patient.last_name }}</h2>
            <div class="tl-meta">
              <span><i class="mdi mdi-card-account-details"></i> {{ data.patient.patient_no }}</span>
              <span><i class="mdi mdi-cake-variant"></i> DOB {{ formatDate(data.patient.dob) }}</span>
              <span><i class="mdi mdi-account"></i> {{ data.patient.gender }}</span>
              <span class="tl-age">{{ data.age_display }}</span>
              <span v-if="data.patient.mother_name"><i class="mdi mdi-mother-heart"></i> Mother: <a :href="'#/patient/' + data.patient.mother_patient_id + '/timeline'">{{ data.patient.mother_name }}</a></span>
              <span v-if="data.patient.birth_facility_name"><i class="mdi mdi-hospital-building"></i> Born at {{ data.patient.birth_facility_name }}</span>
              <span v-if="data.patient.birth_notification_no"><i class="mdi mdi-certificate-outline"></i> {{ data.patient.birth_notification_no }}</span>
            </div>
          </div>
          <div class="tl-stats">
            <div><strong>{{ data.event_count }}</strong> events</div>
            <div class="tl-muted">Across {{ phaseCount }} life phases</div>
          </div>
        </div>

        <!-- FILTER CHIPS -->
        <div class="tl-filter-row">
          <button v-for="f in filters" :key="f.key" class="tl-filter-chip" :class="{ active: activeFilter === f.key }" @click="activeFilter = f.key">
            <i :class="f.icon"></i> {{ f.label }}
            <span class="tl-chip-count">{{ countFor(f.key) }}</span>
          </button>
        </div>

        <!-- TIMELINE -->
        <div v-if="!filteredEvents.length" class="tl-empty">
          <i class="mdi mdi-magnify-close"></i>
          <p>No events match the filter.</p>
        </div>

        <div v-else class="tl-track">
          <div v-for="phase in groupedEvents" :key="phase.key">
            <div class="tl-phase">
              <i :class="phaseIcon(phase.key)"></i>
              <span class="tl-phase-label">{{ phaseLabel(phase.key) }}</span>
              <span class="tl-phase-range">{{ phaseRange(phase) }}</span>
              <span class="tl-phase-count">{{ phase.events.length }}</span>
            </div>
            <div v-for="(e, i) in phase.events" :key="phase.key + '-' + i" class="tl-event" :class="'tl-event-' + e.severity">
              <div class="tl-dot" :style="{ background: e.color }"><i :class="'mdi ' + e.icon"></i></div>
              <div class="tl-card">
                <div class="tl-card-head">
                  <strong>{{ e.title }}</strong>
                  <span class="tl-time">{{ formatDateTime(e.timestamp) }} · {{ relativeAge(e.timestamp) }}</span>
                </div>
                <div class="tl-sub">{{ e.subtitle }}</div>
                <div v-if="e.detail" class="tl-detail">{{ e.detail }}</div>
                <a v-if="e.link" :href="'#' + e.link" class="tl-link"><i class="mdi mdi-open-in-new"></i> Open</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "PatientTimeline",
  components: { Navigation },
  data() {
    return {
      patientId: null,
      activeFilter: 'all',
      data: { patient: null, events: [], age_display: '', event_count: 0 },
      filters: [
        { key: 'all',        label: 'All events',        icon: 'mdi mdi-view-list' },
        { key: 'pregnancy',  label: 'Pregnancy & birth', icon: 'mdi mdi-baby-carriage' },
        { key: 'growth',     label: 'Growth & dev',      icon: 'mdi mdi-chart-bell-curve' },
        { key: 'preventive', label: 'Preventive care',   icon: 'mdi mdi-needle' },
        { key: 'sick',       label: 'Sick visits',       icon: 'mdi mdi-stethoscope' },
        { key: 'alerts',     label: 'Alerts',            icon: 'mdi mdi-alert' }
      ]
    };
  },
  computed: {
    filteredEvents() {
      if (this.activeFilter === 'all') return this.data.events || [];
      const groups = {
        pregnancy:  ['mother_anc','mother_pmtct_enrolled','birth','pnc_visit','own_anc','own_delivery','own_pmtct','child_born'],
        growth:     ['growth','milestone','sam_enrolled','sam_discharge'],
        preventive: ['immunization','hei_dbs','cancer_screen'],
        sick:       ['imci','admission'],
        alerts:     ['alert','death']
      };
      const types = groups[this.activeFilter] || [];
      return (this.data.events || []).filter(e => types.includes(e.event_type));
    },
    groupedEvents() {
      const order = ['in_utero','newborn','childhood','reproductive','clinical','end_of_life'];
      const groups = {};
      (this.filteredEvents || []).forEach(e => {
        const k = e.phase || 'clinical';
        if (!groups[k]) groups[k] = { key: k, events: [] };
        groups[k].events.push(e);
      });
      return order.filter(k => groups[k]).map(k => groups[k]);
    },
    phaseCount() {
      return new Set((this.data.events || []).map(e => e.phase || 'clinical')).size;
    }
  },
  mounted() {
    this.patientId = this.$route.params.id;
    this.load();
  },
  methods: {
    async load() {
      const r = await executeJson({ function: 'getPatientTimeline', patient_id: this.patientId }, hms);
      if (r.data && r.data.success) this.data = r.data.data || this.data;
    },
    countFor(key) {
      if (key === 'all') return (this.data.events || []).length;
      const groups = {
        pregnancy: ['mother_anc','mother_pmtct_enrolled','birth','pnc_visit','own_anc','own_delivery','own_pmtct','child_born'],
        growth: ['growth','milestone','sam_enrolled','sam_discharge'],
        preventive: ['immunization','hei_dbs','cancer_screen'],
        sick: ['imci','admission'],
        alerts: ['alert','death']
      };
      const types = groups[key] || [];
      return (this.data.events || []).filter(e => types.includes(e.event_type)).length;
    },
    phaseLabel(k) { return ({ in_utero:'In utero', newborn:'Newborn period (0-28d)', childhood:'Infancy & childhood', reproductive:'Reproductive age', clinical:'Clinical events', end_of_life:'End of life' })[k] || k; },
    phaseIcon(k) { return ({ in_utero:'mdi mdi-mother-heart', newborn:'mdi mdi-baby-face', childhood:'mdi mdi-human-child', reproductive:'mdi mdi-account-heart', clinical:'mdi mdi-medical-bag', end_of_life:'mdi mdi-grave-stone' })[k] || 'mdi mdi-circle'; },
    phaseRange(phase) {
      const evs = phase.events || [];
      if (!evs.length) return '';
      const first = evs[0].timestamp;
      const last = evs[evs.length - 1].timestamp;
      if (first.substring(0,10) === last.substring(0,10)) return this.formatDate(first);
      return this.formatDate(first) + ' → ' + this.formatDate(last);
    },
    formatDate(d) { if (!d) return ''; const dt = new Date((d+'').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleDateString(); },
    formatDateTime(d) { if (!d) return ''; const dt = new Date((d+'').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleString(); },
    relativeAge(eventTs) {
      if (!this.data.patient || !this.data.patient.dob) return '';
      const dob = new Date((this.data.patient.dob + '').replace(' ','T'));
      const ts = new Date((eventTs + '').replace(' ','T'));
      const diffDays = Math.floor((ts - dob) / 86400000);
      if (diffDays < 0) {
        const before = Math.abs(diffDays);
        return 'in utero (' + before + 'd before birth)';
      }
      if (diffDays < 1) return 'day of birth';
      if (diffDays < 30) return 'day ' + diffDays;
      const months = diffDays / 30.4375;
      if (months < 24) return Math.floor(months) + 'mo old';
      return Math.floor(months / 12) + 'y old';
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.tl-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.tl-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: none; font-size: 13px; font-weight: 500; cursor: pointer; }
.tl-btn-blue { background: #2b6cb0; color: #fff; }

.tl-empty { text-align: center; padding: 80px 20px; color: #a0aec0; }
.tl-empty i { font-size: 48px; }

.tl-header { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 18px 20px; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 14px; }
.tl-name { margin: 0 0 6px; font-size: 24px; color: #2d3748; }
.tl-meta { display: flex; flex-wrap: wrap; gap: 14px; color: #4a5568; font-size: 13px; }
.tl-meta a { color: #2b6cb0; text-decoration: none; }
.tl-age { background: #bee3f8; color: #2a4365; padding: 2px 10px; border-radius: 10px; font-weight: 600; }
.tl-stats { text-align: right; font-size: 13px; color: #4a5568; }
.tl-stats strong { font-size: 24px; color: #2d3748; display: block; }
.tl-muted { color: #a0aec0; font-size: 12px; }

.tl-filter-row { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px; }
.tl-filter-chip { background: #fff; border: 1px solid #e2e8f0; border-radius: 18px; padding: 6px 14px; font-size: 13px; color: #4a5568; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.tl-filter-chip:hover { border-color: #2b6cb0; color: #2b6cb0; }
.tl-filter-chip.active { background: #2b6cb0; color: #fff; border-color: #2b6cb0; }
.tl-chip-count { background: rgba(0,0,0,.1); padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.tl-filter-chip.active .tl-chip-count { background: rgba(255,255,255,.25); }

.tl-track { position: relative; padding-left: 14px; }
.tl-phase { display: flex; align-items: center; gap: 8px; padding: 12px 0; margin-top: 16px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #4a5568; border-top: 2px solid #e2e8f0; }
.tl-phase i { font-size: 18px; color: #2b6cb0; }
.tl-phase-label { color: #2d3748; }
.tl-phase-range { color: #a0aec0; font-weight: 500; text-transform: none; margin-left: 6px; }
.tl-phase-count { margin-left: auto; background: #edf2f7; color: #4a5568; padding: 2px 10px; border-radius: 10px; }

.tl-event { display: flex; gap: 12px; padding: 8px 0; position: relative; }
.tl-event::before { content: ''; position: absolute; left: 17px; top: 0; bottom: 0; width: 2px; background: #e2e8f0; }
.tl-event:first-child::before { top: 18px; }
.tl-event:last-child::before { bottom: 50%; }
.tl-dot { position: relative; z-index: 1; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
.tl-dot i { font-size: 18px; }
.tl-card { flex: 1; background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 14px; }
.tl-card-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 4px; }
.tl-card-head strong { color: #2d3748; font-size: 14px; }
.tl-time { color: #a0aec0; font-size: 11px; white-space: nowrap; }
.tl-sub { color: #4a5568; font-size: 13px; }
.tl-detail { color: #718096; font-size: 12px; margin-top: 3px; }
.tl-link { display: inline-block; margin-top: 6px; color: #2b6cb0; font-size: 12px; text-decoration: none; }
.tl-link:hover { text-decoration: underline; }

.tl-event-critical .tl-card { border-left: 3px solid #dc2626; }
.tl-event-warning  .tl-card { border-left: 3px solid #ea580c; }
.tl-event-success  .tl-card { border-left: 3px solid #22c55e; }
.tl-event-info     .tl-card { border-left: 3px solid #2b6cb0; }
</style>
